// frontend/src/utils/pinnedTotals.js
// Helpers for AG Grid's summary rows: the pinned-bottom totals row and a grouped
// grid's group rows. SSRM grids pass the backend's full-dataset `totals` to
// buildPinnedTotalsRow; client-side grids compute them with sumFields first.

/**
 * Build a pinned-bottom row payload suitable for AG Grid's pinnedBottomRowData prop.
 *
 * The "row" is a single object whose keys must match column field names.
 * Columns whose field is not in `totals` render blank in the pinned row.
 * The column matching `labelField` carries the label text.
 *
 * Returns an array with the single row, or an empty array if totals is
 * empty/missing. Every source of totals answers an empty set with `{}` or
 * none — the backend's grid endpoints and sumFields — so an empty grid pins
 * no row. AG Grid's `pinnedBottomRowData` is typed `any[]` and the
 * runtime iterates it — passing `null` throws `TypeError: e is not iterable`
 * during the next render pass.
 *
 * The returned row also carries an `__isPinnedTotal: true` marker that
 * cell renderers and `getRowClass` callbacks can use to style differently.
 */
export function buildPinnedTotalsRow(totals, labelField, labelText) {
  if (!totals || Object.keys(totals).length === 0) return [];
  return [{ __isPinnedTotal: true, ...totals, [labelField]: labelText }];
}

/**
 * Sum numeric fields across rows. No rows sum to `{}`, not to zeros: an empty
 * grid has nothing to total, so it pins no row.
 */
export function sumFields(rows, fields) {
  if (rows.length === 0) return {};
  return Object.fromEntries(fields.map((f) => [f, rows.reduce((sum, row) => sum + row[f], 0)]));
}

/**
 * True for the pinned-bottom totals row. A total is a derived aggregate of the
 * rows above it, so it must never be editable — typing into it produces a value
 * with no write-back target that vanishes on the next recompute.
 *
 * Every editable column on a grid that renders a totals row MUST gate its
 * `editable` callback through this. The `child_count == null` / `!isGroupRow`
 * "is this a leaf" proxies do NOT exclude the pinned row (it carries no
 * `child_count`). Checks both the AG Grid `node.rowPinned` flag and our own
 * `__isPinnedTotal` marker.
 */
export function isPinnedTotalRow(params) {
  return Boolean(params?.node?.rowPinned || params?.data?.__isPinnedTotal);
}

/**
 * Helper for AG Grid `getRowClass` to identify pinned-total rows so they can
 * receive distinct styling (heavier border, semi-bold font, etc.).
 */
export function pinnedTotalRowClass(params) {
  if (params.node?.rowPinned === 'bottom' || params.data?.__isPinnedTotal) {
    return 'ag-row-pinned-total';
  }
  return undefined;
}

/**
 * Wrap a column's `editable` rule so the pinned totals row is never editable,
 * whatever the column's own logic says. A total is a derived aggregate with no
 * writable record behind it — letting it enter edit mode produces a value that
 * vanishes on the next recompute. Boolean `editable: true` and callback forms
 * are both handled; `false`/undefined columns are left untouched by the caller.
 */
export function pinnedAwareEditable(originalEditable) {
  return (params) => {
    if (isPinnedTotalRow(params)) return false;
    return typeof originalEditable === 'function'
      ? originalEditable(params)
      : Boolean(originalEditable);
  };
}

/**
 * True for a cell with no value on a summary row: the pinned totals row or a
 * group row (a grouped grid's product row). A formatter's fallback for a
 * record's missing value says something about that record ("Never sold", "No
 * demand", a dash), and a summary row is no record, so its cell stays empty.
 */
export function isEmptySummaryCell(params) {
  return Boolean(params.node?.rowPinned || params.node?.group) && params.value == null;
}

/**
 * Wrap an existing valueFormatter so a summary row's cell with no value renders
 * empty ({@link isEmptySummaryCell}) instead of the formatter's fallback for a
 * record.
 *
 * Applied via {@link wrapColumnsForSummaryRows} to columns that already
 * carry an inline `valueFormatter`.
 */
export function summaryAwareFormatter(originalFormatter) {
  return (params) => (isEmptySummaryCell(params) ? '' : originalFormatter(params));
}

/**
 * Walk a column-defs tree and make every leaf column summary-row aware. Use
 * this on grids that render a pinned-bottom totals row or group rows. Two
 * things happen per leaf column:
 *
 *  1. `editable` is wrapped so the totals row can never enter edit mode (see
 *     {@link pinnedAwareEditable}), whatever a new column declares.
 *  2. `valueFormatter` (when present inline) is wrapped so a summary row with
 *     no value renders empty (see {@link summaryAwareFormatter}).
 *
 * Columns that rely on a `type:` from {@link useColumnTypes} for formatting
 * (no inline `valueFormatter`) are intentionally skipped for (2) — installing
 * an inline formatter here would override the type's formatter (AG Grid
 * resolves inline `valueFormatter` before the type-level one). Each type in
 * `useColumnTypes` owns its summary-row null behavior directly via
 * {@link isEmptySummaryCell}.
 *
 * Custom `cellRenderer` columns are still expected to handle summary rows on
 * their own (most read `params.value` and naturally render empty for null;
 * those that read row metadata — e.g. `OverrideCellRenderer` — guard on the
 * node).
 */
export function wrapColumnsForSummaryRows(columnDefs) {
  if (!Array.isArray(columnDefs)) return columnDefs;
  return columnDefs.map((col) => {
    if (!col) return col;
    if (col.children) {
      return { ...col, children: wrapColumnsForSummaryRows(col.children) };
    }
    let wrapped = col;
    if (col.editable) {
      wrapped = { ...wrapped, editable: pinnedAwareEditable(col.editable) };
    }
    if (typeof col.valueFormatter === 'function') {
      wrapped = { ...wrapped, valueFormatter: summaryAwareFormatter(col.valueFormatter) };
    }
    return wrapped;
  });
}
